import { supabase as _supabase } from './supabase';

const db = _supabase as any;

export interface Goal {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'active' | 'completed' | 'paused' | 'abandoned';
  target_date?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Habit {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  frequency: 'daily' | 'weekly';
  target_days_per_week: number;
  is_active: boolean;
  goal_id?: string;
  // Habit Installation OS™ wizard fields
  outcome?: string;
  linked_pattern?: string;
  identity_shift?: string;
  minimum_version?: string;
  ideal_version?: string;
  trigger_cue?: string;
  obstacle_plan?: string;
  reward?: string;
  created_by_coach?: boolean;
  coach_id?: string;
  created_at: string;
  updated_at: string;
  completions?: HabitCompletion[];
}

export interface HabitCompletion {
  id: string;
  habit_id: string;
  user_id: string;
  completed_date: string;
  notes?: string;
  created_at: string;
}

export interface Task {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'medium' | 'high';
  due_date?: string;
  goal_id?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CheckIn {
  id: string;
  user_id: string;
  energy_level: number;
  mood_level: number;
  focus_level: number;
  notes?: string;
  checked_in_at: string;
  created_at: string;
}

export interface JournalEntry {
  id: string;
  user_id: string;
  title: string;
  content: string;
  mood?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface Milestone {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  achieved_at: string;
  goal_id?: string;
  created_at: string;
}

// Goals
export async function fetchGoals(userId: string): Promise<Goal[]> {
  const { data } = await db.from('goals').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  return data || [];
}

export async function createGoal(userId: string, payload: Partial<Goal>): Promise<Goal | null> {
  const { data } = await db.from('goals').insert({ ...payload, user_id: userId }).select().single();
  return data;
}

export async function updateGoal(id: string, payload: Partial<Goal>): Promise<void> {
  await db.from('goals').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id);
}

export async function deleteGoal(id: string): Promise<void> {
  await db.from('goals').delete().eq('id', id);
}

// Habits
export async function fetchHabits(userId: string): Promise<Habit[]> {
  const { data: habits } = await db.from('habits').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  if (!habits) return [];
  const today = new Date();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  const { data: completions } = await db
    .from('habit_completions')
    .select('*')
    .eq('user_id', userId)
    .gte('completed_date', weekStart.toISOString().split('T')[0]);
  return habits.map((h: Habit) => ({
    ...h,
    completions: (completions || []).filter((c: HabitCompletion) => c.habit_id === h.id),
  }));
}

export async function createHabit(userId: string, payload: Partial<Habit>): Promise<Habit | null> {
  const { data } = await db.from('habits').insert({ ...payload, user_id: userId }).select().single();
  return data;
}

export async function updateHabit(id: string, payload: Partial<Habit>): Promise<void> {
  await db.from('habits').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id);
}

export async function toggleHabitCompletion(habitId: string, userId: string, date: string): Promise<boolean> {
  const { data: existing } = await db
    .from('habit_completions')
    .select('id')
    .eq('habit_id', habitId)
    .eq('user_id', userId)
    .eq('completed_date', date)
    .maybeSingle();
  if (existing) {
    await db.from('habit_completions').delete().eq('id', existing.id);
    return false;
  }
  await db.from('habit_completions').insert({ habit_id: habitId, user_id: userId, completed_date: date });
  return true;
}

// Tasks
export async function fetchTasks(userId: string): Promise<Task[]> {
  const { data } = await db.from('tasks').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  return data || [];
}

export async function createTask(userId: string, payload: Partial<Task>): Promise<Task | null> {
  const { data } = await db.from('tasks').insert({ ...payload, user_id: userId }).select().single();
  return data;
}

export async function updateTask(id: string, payload: Partial<Task>): Promise<void> {
  await db.from('tasks').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id);
}

export async function deleteTask(id: string): Promise<void> {
  await db.from('tasks').delete().eq('id', id);
}

// Check-ins
export async function fetchCheckIns(userId: string, limit = 30): Promise<CheckIn[]> {
  const { data } = await db
    .from('check_ins')
    .select('*')
    .eq('user_id', userId)
    .order('checked_in_at', { ascending: false })
    .limit(limit);
  return data || [];
}

export async function createCheckIn(userId: string, payload: Partial<CheckIn>): Promise<CheckIn | null> {
  const { data } = await db.from('check_ins').insert({ ...payload, user_id: userId }).select().single();
  return data;
}

// Journal
export async function fetchJournalEntries(userId: string): Promise<JournalEntry[]> {
  const { data } = await db.from('journal_entries').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  return data || [];
}

export async function createJournalEntry(userId: string, payload: Partial<JournalEntry>): Promise<JournalEntry | null> {
  const { data } = await db.from('journal_entries').insert({ ...payload, user_id: userId }).select().single();
  return data;
}

export async function updateJournalEntry(id: string, payload: Partial<JournalEntry>): Promise<void> {
  await db.from('journal_entries').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id);
}

export async function deleteJournalEntry(id: string): Promise<void> {
  await db.from('journal_entries').delete().eq('id', id);
}

// Milestones
export async function fetchMilestones(userId: string): Promise<Milestone[]> {
  const { data } = await db.from('milestones').select('*').eq('user_id', userId).order('achieved_at', { ascending: false });
  return data || [];
}

export async function createMilestone(userId: string, payload: Partial<Milestone>): Promise<Milestone | null> {
  const { data } = await db.from('milestones').insert({ ...payload, user_id: userId }).select().single();
  return data;
}

export async function deleteMilestone(id: string): Promise<void> {
  await db.from('milestones').delete().eq('id', id);
}

// Coach read helpers (fetch for a specific user_id, used by coaches viewing clients)
export async function fetchGoalsForUser(userId: string): Promise<Goal[]> {
  const { data } = await db.from('goals').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  return data || [];
}

export async function fetchHabitsForUser(userId: string): Promise<Habit[]> {
  const { data: habits } = await db.from('habits').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  if (!habits) return [];
  const { data: completions } = await db.from('habit_completions').select('*').eq('user_id', userId).order('completed_date', { ascending: false }).limit(200);
  return habits.map((h: Habit) => ({ ...h, completions: (completions || []).filter((c: HabitCompletion) => c.habit_id === h.id) }));
}

export async function fetchTasksForUser(userId: string): Promise<Task[]> {
  const { data } = await db.from('tasks').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  return data || [];
}

export async function fetchCheckInsForUser(userId: string, limit = 30): Promise<CheckIn[]> {
  const { data } = await db.from('check_ins').select('*').eq('user_id', userId).order('checked_in_at', { ascending: false }).limit(limit);
  return data || [];
}

export async function fetchMilestonesForUser(userId: string): Promise<Milestone[]> {
  const { data } = await db.from('milestones').select('*').eq('user_id', userId).order('achieved_at', { ascending: false });
  return data || [];
}

export async function fetchJournalEntriesForUser(userId: string, limit = 10): Promise<JournalEntry[]> {
  const { data } = await db.from('journal_entries').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(limit);
  return data || [];
}

export async function fetchCheckInsForUserSince(userId: string, since: string): Promise<CheckIn[]> {
  const { data } = await db.from('check_ins').select('*').eq('user_id', userId).gte('checked_in_at', since).order('checked_in_at', { ascending: true });
  return data || [];
}

// Coach creates a habit plan for a respondent (Habit Installation OS™)
export async function createHabitForUser(targetUserId: string, coachId: string, payload: Partial<Habit>): Promise<Habit | null> {
  const { data } = await db.from('habits').insert({
    ...payload,
    user_id: targetUserId,
    coach_id: coachId,
    created_by_coach: true,
    is_active: true,
  }).select().single();
  return data;
}

// Fetch coach-created habits for a respondent (for coach review)
export async function fetchCoachCreatedHabitsForUser(userId: string): Promise<Habit[]> {
  const { data: habits } = await db.from('habits').select('*').eq('user_id', userId).eq('created_by_coach', true).order('created_at', { ascending: false });
  if (!habits) return [];
  const { data: completions } = await db.from('habit_completions').select('*').eq('user_id', userId).order('completed_date', { ascending: false }).limit(200);
  return habits.map((h: Habit) => ({ ...h, completions: (completions || []).filter((c: HabitCompletion) => c.habit_id === h.id) }));
}
